import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Wand2, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import JournalNewsletter from "../components/JournalNewsletter";
import useSeo from "../lib/useSeo";
import { findPostBySlug, PUBLISHED_POSTS, getCategoryColor } from "../data/journal";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

/**
 * Render a body block. Blocks come from `data/journal.js` — see the header
 * comment there for the DSL spec.
 */
function Block({ block }) {
  if (!block) return null;
  switch (block.type) {
    case "h2":
      return (
        <h2 className="font-display font-black text-3xl md:text-4xl mt-12 mb-4 text-pb-ink leading-[1.1]">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-display font-black text-xl md:text-2xl mt-8 mb-2 text-pb-ink leading-tight">
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p
          className="mt-4 text-pb-ink/85 text-[17px] leading-[1.75]"
          dangerouslySetInnerHTML={{ __html: block.text }}
        />
      );
    case "quote":
      return (
        <blockquote className="mt-8 mb-8 border-l-4 border-pb-coral pl-6 py-2 italic text-xl text-pb-ink font-display leading-snug">
          &ldquo;{block.text}&rdquo;
          {block.by && (
            <cite className="block mt-3 not-italic text-xs font-extrabold uppercase tracking-widest text-pb-coral">
              — {block.by}
            </cite>
          )}
        </blockquote>
      );
    case "list":
      return (
        <ul className="mt-4 space-y-2.5 text-[17px] leading-[1.7] text-pb-ink/85">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="pl-6 relative"
              dangerouslySetInnerHTML={{
                __html: `<span class="absolute left-0 top-2 h-2 w-2 rounded-full bg-pb-coral"></span>${item}`,
              }}
            />
          ))}
        </ul>
      );
    case "num":
      return (
        <ol className="mt-4 space-y-4 text-[17px] leading-[1.7] text-pb-ink/85">
          {block.items.map((item, i) => (
            <li key={i} className="pl-10 relative">
              <span className="absolute left-0 top-0 h-7 w-7 rounded-full bg-pb-ink text-white text-xs font-black flex items-center justify-center">
                {i + 1}
              </span>
              <span dangerouslySetInnerHTML={{ __html: item }} />
            </li>
          ))}
        </ol>
      );
    case "callout": {
      const toneMap = {
        coral: { bg: "#FFE9E0", border: "#F26A34", eyebrow: "#F26A34" },
        aqua: { bg: "#D6F4F8", border: "#7CC5D4", eyebrow: "#3A7A88" },
        sun: { bg: "#FFF6D6", border: "#FFC15E", eyebrow: "#B57415" },
      };
      const t = toneMap[block.tone] || toneMap.coral;
      return (
        <aside
          className="mt-8 rounded-2xl p-6 border-l-4"
          style={{ background: t.bg, borderColor: t.border }}
        >
          {block.title && (
            <p
              className="text-[11px] font-extrabold uppercase tracking-widest"
              style={{ color: t.eyebrow }}
            >
              {block.title}
            </p>
          )}
          <p className="mt-1 text-[17px] leading-[1.65] text-pb-ink">{block.text}</p>
        </aside>
      );
    }
    case "cta":
      return (
        <div className="mt-10 rounded-3xl p-6 md:p-8 border-2 border-dashed border-pb-coral/40 bg-white">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="h-12 w-12 rounded-2xl bg-pb-coral/10 flex items-center justify-center shrink-0">
              <Wand2 className="h-5 w-5 text-pb-coral" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-black text-lg text-pb-ink leading-tight">
                {block.label}
              </p>
              {block.sub && (
                <p className="text-sm text-pb-soft mt-0.5">{block.sub}</p>
              )}
            </div>
            <Link
              to={block.href}
              data-testid="journal-inline-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-pb-coral text-white font-black text-sm px-5 py-2.5 shadow hover:scale-105 transition whitespace-nowrap"
            >
              Try it <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      );
    case "image":
      return (
        <figure className="mt-8">
          <img
            src={block.src}
            alt={block.alt}
            className="w-full rounded-2xl border border-pb-ink/5"
          />
          {block.caption && (
            <figcaption className="mt-2 text-xs text-pb-soft text-center italic">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    default:
      return null;
  }
}

export default function JournalPost() {
  const { slug } = useParams();
  const post = findPostBySlug(slug);

  useSeo({
    title: post
      ? `${post.title} | Plate Buddies Journal`
      : "Story not found | Plate Buddies Journal",
    description: post?.excerpt || "A Plate Buddies journal story.",
    image: post?.hero_image,
    url: post
      ? `https://platebuddies.com/journal/${post.slug}`
      : "https://platebuddies.com/journal",
    type: "article",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-pb-cream font-body">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="font-display font-black text-4xl">Story not found</h1>
          <p className="text-pb-soft mt-3">That story may have wandered off.</p>
          <Link
            to="/journal"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-bold px-6 py-3"
          >
            <ArrowLeft className="h-4 w-4" /> Back to the journal
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Article JSON-LD for Google rich results.
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: [post.hero_image],
    author: { "@type": "Organization", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "Plate Buddies",
      logo: {
        "@type": "ImageObject",
        url: "https://platebuddies.com/brand/logo-wordmark.png",
      },
    },
    datePublished: post.published_at,
    dateModified: post.published_at,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://platebuddies.com/journal/${post.slug}`,
    },
    keywords: post.tags.join(", "),
  };

  // Related posts (up to 3, exclude current)
  const related = PUBLISHED_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="font-body min-h-screen bg-pb-cream">
      <Navbar />
      <JsonLd id={`journal-article-${post.slug}`} data={articleSchema} />

      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-16">
        <Link
          to="/journal"
          data-testid="journal-back"
          className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-widest text-pb-soft hover:text-pb-coral"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> The Journal
        </Link>

        <div className="mt-4">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest text-white"
            style={{ background: getCategoryColor(post.category) }}
          >
            <Sparkles className="h-3 w-3" /> {post.category}
          </span>
        </div>

        <h1
          data-testid="journal-post-title"
          className="font-display font-black text-4xl md:text-5xl mt-4 leading-[1.05] text-pb-ink"
        >
          {post.title}
        </h1>
        {post.subtitle && (
          <p className="mt-5 text-xl text-pb-soft leading-[1.5]">{post.subtitle}</p>
        )}

        <div className="mt-6 flex items-center gap-4 text-xs text-pb-soft">
          <span className="font-bold text-pb-ink">{post.author}</span>
          <span>·</span>
          <span>{formatDate(post.published_at)}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1 font-bold">
            <Clock className="h-3 w-3" /> {post.read_time_min} min read
          </span>
        </div>

        <figure className="mt-8 rounded-[2rem] overflow-hidden border-[6px] border-white shadow-[0_20px_50px_rgba(28,22,40,0.12)]">
          <img
            src={post.hero_image}
            alt={post.hero_alt}
            className="w-full h-auto"
          />
        </figure>

        <div className="mt-8">
          {post.body.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </div>

        {/* Inline newsletter — retention play */}
        <JournalNewsletter variant="inline" />

        {post.tags?.length > 0 && (
          <div className="mt-12 pt-6 border-t border-pb-ink/10 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span
                key={t}
                className="px-3 py-1 rounded-full bg-white text-pb-ink text-xs font-bold border border-pb-ink/10"
              >
                #{t.replace(/\s+/g, "-")}
              </span>
            ))}
          </div>
        )}
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-16">
          <h2 className="font-display font-black text-2xl md:text-3xl text-pb-ink">Keep reading</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/journal/${p.slug}`}
                data-testid={`journal-related-${p.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-[0_12px_28px_rgba(28,22,40,0.07)] hover:-translate-y-1 hover:shadow-[0_20px_42px_rgba(28,22,40,0.12)] transition-all"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={p.hero_image}
                    alt={p.hero_alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span
                    className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest text-white shadow"
                    style={{ background: getCategoryColor(p.category) }}
                  >
                    {p.category}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-black text-base leading-tight text-pb-ink group-hover:text-pb-coral transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-xs text-pb-soft line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
